import React from "react";
import PromiseService from "../../Services/Shared/Promise.service";
import { MAKE_SERVICES_COLLECTOR_EMPTY_CONTEXT, } from "./makeServicesCollector";
import useUniqueId from "./useUniqueId";
export const EMPTY_SUCCESSFUL_SERVICE_STATE = {
    isLoading: false,
    data: null,
    wasSuccessful: true,
    hasError: false,
    errorMessage: null,
};
const getErrorMessage = (error) => {
    if (typeof error === "string") {
        return error;
    }
    if (typeof error === "object" && error.message) {
        return error.message;
    }
    return null;
};
export const USE_SERVICE_REDUCER = (state, action) => {
    switch (action.type) {
        case "START_FETCH":
            return Object.assign(Object.assign({}, state), { isLoading: true, wasSuccessful: false, hasError: false, errorMessage: null });
        case "GOT_DATA": {
            return {
                isLoading: false,
                wasSuccessful: true,
                hasError: false,
                errorMessage: null,
                data: action.payload.response,
            };
        }
        case "ERROR": {
            return Object.assign(Object.assign({}, state), { isLoading: false, wasSuccessful: false, hasError: true, errorMessage: action.payload.errorMessage });
        }
        case "CLEAR_RESULTS": {
            // Reset everything except for isLoading.  Because we can't stop a process, it's better to maintain the fact
            // that we are in a loading state.
            return Object.assign(Object.assign({}, state), { hasError: false, errorMessage: null, wasSuccessful: false, data: null });
        }
        case "EDIT_DATA": {
            return Object.assign(Object.assign({}, state), { data: typeof action.payload === "function"
                    ? // $FlowFixMe Flow has trouble converting mixed to a function with the same parameters.  ignore for now.
                        // @ts-expect-error - TS2349 - This expression is not callable.
                        action.payload(state.data)
                    : action.payload });
        }
        default:
            return state;
    }
};
const defaultConfig = {};
export const GET_USE_SERVICE_INITIAL_STATE = ({ initialData, }) => {
    return {
        isLoading: false,
        data: initialData || null,
        wasSuccessful: false,
        hasError: false,
        errorMessage: null,
    };
};
/**
 * **Makes it easier to display loading and error states for asynchronous actions.**
 * Pass in a function which returns a promise and this hook will return a tuple of
 * a loading state, which represents the progress of this action, a refresh method, and an object containing additional actions you can take.
 * For more info on that object, look at UseServiceActions.
 * This refresh method accepts the same arguments as the service method.  You can treat it
 * just like the original method, but when called, the loadingState with update appropriately.
 * The clear method will reset the loadingState (except for isLoading as it does not stop a process already in progress).
 *
 * @param {(any) => Promise} serviceMethod
 * @param {Config} config - the config for useService.
 */
const useService = (serviceMethod, config) => {
    var _a, _b;
    const safeConfig = React.useMemo(() => (Object.assign(Object.assign({}, defaultConfig), config)), [config]);
    const [servicesCollector] = React.useState((_a = safeConfig.servicesCollector) !== null && _a !== void 0 ? _a : MAKE_SERVICES_COLLECTOR_EMPTY_CONTEXT);
    const { dispatch: collectorDispatch, state: collectorState, isEmpty: isServicesCollectorEmpty, } = React.useContext(servicesCollector);
    let collectorId = useUniqueId()[0];
    if (safeConfig.serviceCollectorId) {
        collectorId = safeConfig.serviceCollectorId;
    }
    const [initialState] = React.useState(GET_USE_SERVICE_INITIAL_STATE({
        initialData: safeConfig.initialData,
    }));
    const [state, internalDispatch] = React.useReducer(USE_SERVICE_REDUCER, initialState);
    React.useEffect(() => {
        if (!isServicesCollectorEmpty) {
            collectorDispatch({
                type: "RESET",
                payload: { id: collectorId, state: initialState },
            });
        }
        return () => {
            collectorDispatch({
                type: "REMOVE",
                payload: { id: collectorId },
            });
        };
    }, [collectorDispatch, collectorId, initialState, isServicesCollectorEmpty]);
    const dispatch = React.useMemo(() => isServicesCollectorEmpty
        ? internalDispatch
        : // @ts-expect-error - TS7006 - Parameter 'action' implicitly has an 'any' type.
            (action) => collectorDispatch({
                type: "USE_SERVICE_ACTION",
                payload: { id: collectorId, useServiceAction: action },
            }), [collectorDispatch, collectorId, isServicesCollectorEmpty]);
    const serviceRef = React.useRef(serviceMethod);
    React.useEffect(() => {
        serviceRef.current = serviceMethod;
    }, [serviceMethod]);
    /*
     * When refresh is called a bunch of times, we want to only pay attention to the most recent call and ignore the results
     * of previously made requests.  We store a method to ignore the last promise in a ref.
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const ignoreLastPromise = React.useRef(() => { });
    const refresh = React.useMemo(() => (...args) => {
        const promise = serviceRef.current(...args);
        if (!promise) {
            if (console.error) {
                console.error(new Error("No promise returned by service method"));
            }
            return Promise.reject("No promise returned by service method");
        }
        dispatch({ type: "START_FETCH", payload: undefined });
        const [ignorablePromise, ignore] = PromiseService.createIgnorablePromise(promise);
        // Ignore the last promise.
        ignoreLastPromise.current();
        // Set the ref to ignore this promise.
        ignoreLastPromise.current = ignore;
        // Return the ignorable promise.
        return ignorablePromise
            .then((response) => {
            dispatch({ type: "GOT_DATA", payload: { response } });
            return response;
        })
            .catch((error) => {
            dispatch({
                type: "ERROR",
                payload: {
                    errorMessage: getErrorMessage(error) ||
                        safeConfig.genericErrorMessage ||
                        null,
                },
            });
            throw error;
        });
    }, [dispatch, safeConfig.genericErrorMessage]);
    const clear = React.useMemo(() => () => {
        dispatch({ type: "CLEAR_RESULTS", payload: null });
    }, [dispatch]);
    const edit = React.useMemo(() => (update) => {
        dispatch({ type: "EDIT_DATA", payload: update });
    }, [dispatch]);
    return [
        isServicesCollectorEmpty
            ? state
            : (_b = collectorState[collectorId]) !== null && _b !== void 0 ? _b : initialState,
        refresh,
        React.useMemo(() => ({ clear, edit }), [clear, edit]),
    ];
};
export default useService;
